// src/components/LeadForm.js
import React, { useState, useEffect } from 'react';
import { Modal } from './UI';
import { createLead, updateLead, getServices, getUsers, getSettings } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useLeadLookups } from '../hooks/useLeadLookups';

const emptyLead = { name: '', phone: '', email: '', company: '', service: '', source: '', status: '', followup_date: '', notes: '', assigned_to: '' };

export default function LeadForm({ open, onClose, lead, onSaved, toast }) {
  const { user, can } = useAuth();
  // The API also refuses reassignment for 'own'-scoped roles, so showing the
  // picker to them would just be a control that silently does nothing.
  const canReassign = can('leads.reassign') && user?.leads_scope !== 'own';
  const { statuses, sources } = useLeadLookups();
  const [form, setForm] = useState(emptyLead);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [defaultAssignee, setDefaultAssignee] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Mirrors the backend's default-status logic (api/config/lead_lookup_helpers.php
  // defaultStatusForSource()): auto-capture sources (ads/webhooks) default to
  // whichever status is flagged is_default_auto, everything else to is_default.
  const defaultStatusFor = (source) => {
    const isAuto = sources.find(s => s.name === source)?.is_auto_capture;
    const col = Number(isAuto) ? 'is_default_auto' : 'is_default';
    return statuses.find(s => Number(s[col]))?.name || (col === 'is_default_auto' ? 'New' : 'Hot');
  };
  const defaultSource = () => sources.find(s => Number(s.is_default))?.name || sources[0]?.name || '';

  useEffect(() => {
    getServices().then(r => setServices(r.data.map(s => s.name))).catch(() => {});
    // Deactivated users can't work leads, so they don't belong in the picker.
    // The users.view response carries is_active; the trimmed list everyone else
    // gets is already active-only.
    getUsers().then(r => setUsers(r.data.filter(u => u.is_active === undefined || Number(u.is_active) === 1))).catch(() => {});
    getSettings().then(r => setDefaultAssignee(String(r.data.default_assigned_to || ''))).catch(() => {});
  }, []);

  // Re-initialise on open only. The fetched values this seeds from (defaultAssignee,
  // sources, statuses) are deliberately not dependencies: re-running on each one's
  // arrival would wipe whatever the user had already typed into a form that was
  // opened first. The effect below backfills them instead.
  useEffect(() => {
    if (open) {
      setForm(lead
        ? { ...lead, followup_date: lead.followup_date || '', assigned_to: lead.assigned_to || '' }
        : { ...emptyLead, source: defaultSource(), status: defaultStatusFor(defaultSource()), followup_date: '', assigned_to: defaultAssignee });
      setError('');
    }
  }, [open, lead]);

  // Those three all come from fetches that can land after an Add Lead form is
  // already open. Fill the fields they drive once they arrive — but only while the
  // field is still untouched, so nothing the user chose gets overwritten.
  useEffect(() => {
    if (!open || lead?.id) return;
    setForm(f => {
      const next = { ...f };
      if (!f.assigned_to && defaultAssignee) next.assigned_to = defaultAssignee;
      if (!f.source) next.source = defaultSource();
      if (!f.status && next.source) next.status = defaultStatusFor(next.source);
      return (next.assigned_to === f.assigned_to && next.source === f.source && next.status === f.status) ? f : next;
    });
  }, [open, lead, defaultAssignee, sources, statuses]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // When source changes, auto-update status only if it's still at a default value
  const changeSource = (src) => setForm(f => ({
    ...f,
    source: src,
    status: statuses.some(s => s.name === f.status && (Number(s.is_default) || Number(s.is_default_auto))) ? defaultStatusFor(src) : f.status,
  }));

  const save = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.phone.trim()) { setError('Phone is required'); return; }
    setSaving(true);
    try {
      const saved = lead?.id
        ? (await updateLead(lead.id, form)).data
        : (await createLead(form)).data;
      toast(lead?.id ? 'Lead updated!' : 'New lead added!');
      onSaved(saved);
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={lead?.id ? 'Edit Lead' : 'Add New Lead'}>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Raj Mehta" />
        </div>
        <div className="form-group">
          <label className="form-label">Phone *</label>
          <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 98765 43210" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@company.com" />
        </div>
        <div className="form-group">
          <label className="form-label">Company</label>
          <input className="form-input" value={form.company} onChange={e => set('company', e.target.value)} placeholder="Business name" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Service Interested In</label>
          <select className="form-input" value={form.service} onChange={e => set('service', e.target.value)}>
            <option value="">Select service...</option>
            {services.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Lead Source</label>
          <select className="form-input" value={form.source} onChange={e => changeSource(e.target.value)}>
            {sources.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
            {statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Follow-up Date</label>
          <input className="form-input" type="date" value={form.followup_date} onChange={e => set('followup_date', e.target.value)} />
        </div>
      </div>
      {canReassign && (
        <div className="form-group">
          <label className="form-label">Assign To</label>
          <select className="form-input" value={form.assigned_to || ''} onChange={e => set('assigned_to', e.target.value)}>
            <option value="">— Unassigned —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      )}
      <div className="form-group">
        <label className="form-label">Notes</label>
        <textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any details about this lead..." />
      </div>
      {error && <p className="error-text">{error}</p>}
      <div className="form-actions">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Lead'}</button>
      </div>
    </Modal>
  );
}
